import { useCallback, useEffect, useState } from "react";
import { Download, Eye, FileText, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { uploadFile } from "../../lib/field";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, StatusBadge, LegalBadge, ResponsiveTable } from "../../components/dash/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { LEGAL_DOC_TYPES, LAND_DOC_TYPES, formatDate } from "../../lib/labels";
import { DateField } from "../profit/DateField";

const B = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (r, dl) => `${B}/api/legal-docs/${r.id}/file${dl ? "?download=true" : ""}`;
const sel = "w-full h-11 rounded-md border border-input bg-white px-3 text-sm";
const EMPTY = { kind: "ORG_LEGAL", docType: "SK_KELOMPOK", title: "", landParcelId: "", docNumber: "", docDate: "", grantingParty: "" };
const docLabel = (r) => (r.kind === "LAND_CONTROL" ? `${LAND_DOC_TYPES[r.docType] || "Dokumen Penyerahan / Penguasaan Lahan"} ${r.landCode || ""}` : LEGAL_DOC_TYPES[r.docType]);
const detail = (r) => [r.title, r.docNumber && `No. ${r.docNumber}`, r.docDate, r.grantingParty && `Pemberi: ${r.grantingParty}`, r.receiverName && `Penerima: ${r.receiverName}`, r.areaHectares && `${r.areaHectares} ha`, r.location].filter(Boolean).join(" · ") || "-";

const UploadForm = ({ lands, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const land = f.kind === "LAND_CONTROL";
  const submit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!file) return toast.error("Pilih file dokumen");
    setBusy(true);
    try {
      await uploadFile("/legal-docs", file, land ? f : { ...f, landParcelId: "", docNumber: "", docDate: "", grantingParty: "" });
      toast.success(land ? "Dokumen lahan tersimpan (tanpa persetujuan PT SJI)" : "Dokumen terkirim untuk verifikasi PT SJI");
      setFile(null); setF(EMPTY); form.reset(); onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border rounded-xl p-5 grid gap-4 sm:grid-cols-2" data-testid="legal-upload-form">
      <div><Label>Jenis</Label><select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value, docType: e.target.value === "LAND_CONTROL" ? "PENYERAHAN_LAHAN" : "SK_KELOMPOK" })} className={sel} data-testid="legal-kind-select">
        <option value="ORG_LEGAL">Legalitas Organisasi</option><option value="LAND_CONTROL">Dokumen Lahan (Penyerahan / Penguasaan, Peta, Foto)</option></select></div>
      {land && <div><Label>Jenis dokumen lahan</Label><select value={f.docType} onChange={set("docType")} className={sel} data-testid="legal-land-doctype-select">
        {Object.entries(LAND_DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>}
      {!land && <div><Label>Dokumen</Label><select value={f.docType} onChange={set("docType")} className={sel} data-testid="legal-doctype-select">
        {Object.entries(LEGAL_DOC_TYPES).filter(([k]) => k !== "PENYERAHAN_LAHAN").map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>}
      {land && <>
        <div><Label>Lahan</Label><select required value={f.landParcelId} onChange={set("landParcelId")} className={sel} data-testid="legal-land-select">
          <option value="">Pilih</option>{lands.map((l) => <option key={l.id} value={l.id}>{l.landCode} — {l.name}</option>)}</select></div>
        <div><Label>Nomor dokumen</Label><Input value={f.docNumber} onChange={set("docNumber")} className="h-11" data-testid="legal-docnumber-input" /></div>
        <div><Label>Tanggal dokumen</Label><DateField value={f.docDate} onChange={(v) => setF((p) => ({ ...p, docDate: v }))} testid="legal-docdate-input" fromYear={1950} toYear={new Date().getFullYear() + 5} className="h-11" /></div>
        <div><Label>Pihak pemberi / yang menyerahkan (mis. Ninik Mamak)</Label><Input value={f.grantingParty} onChange={set("grantingParty")} className="h-11" data-testid="legal-granting-input" /></div>
      </>}
      <div><Label>Judul / keterangan</Label><Input value={f.title} onChange={set("title")} className="h-11" data-testid="legal-title-input" /></div>
      <div><Label>File (PDF / foto, maks 20 MB)</Label><Input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-11" data-testid="legal-file-input" /></div>
      <p className="sm:col-span-2 text-xs text-slate-500">{land ? "Dokumen lahan tidak diperiksa atau disetujui PT SJI. Kebenaran data dan legalitas lahan menjadi tanggung jawab Kelompok Tani/Koperasi; PT SJI hanya dapat memantau." : "Legalitas organisasi diperiksa PT SJI dalam sistem."}</p>
      <Button type="submit" disabled={busy} className="sm:col-span-2 h-12 bg-emerald-800 hover:bg-emerald-900 font-bold" data-testid="legal-submit-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}{land ? "SIMPAN DOKUMEN LAHAN" : "KIRIM UNTUK VERIFIKASI PT SJI"}</Button>
    </form>
  );
};

const StatusCard = ({ label, value, testid }) => (
  <div className="bg-white border rounded-xl p-4" data-testid={testid}>
    <p className="text-xs text-slate-500">{label}</p>
    <div className="mt-1.5"><LegalBadge value={value || "NONE"} /></div>
  </div>
);

const FileLinks = ({ r, onPreview }) => (
  <div className="flex flex-wrap gap-2">
    {onPreview && <button type="button" onClick={() => onPreview(r)} className="inline-flex items-center gap-1 text-emerald-800 font-semibold text-sm" data-testid={`legal-preview-${r.id}`}><Eye className="h-4 w-4" />Lihat</button>}
    {!onPreview && <a href={fileUrl(r)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-800 font-semibold text-sm" data-testid={`legal-file-${r.id}`}><FileText className="h-4 w-4" />Buka</a>}
    <a href={fileUrl(r, true)} className="inline-flex items-center gap-1 text-slate-700 font-semibold text-sm" data-testid={`legal-download-${r.id}`}><Download className="h-4 w-4" />Unduh</a>
  </div>
);

const statusCol = { key: "status", label: "Status", render: (r) => <div><StatusBadge value={r.status} testid={`legal-status-${r.id}`} />{r.note && <p className="text-xs text-slate-500 mt-1">Catatan PT SJI: {r.note}</p>}</div> };

function FarmerLegal({ user }) {
  const [rows, setRows] = useState(null);
  const [lands, setLands] = useState([]);
  const [me, setMe] = useState(null);
  const load = useCallback(() => {
    api.get("/legal-docs").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/me/land").then((r) => setLands(r.data)).catch(() => {});
    api.get("/me/legal-status").then((r) => setMe(r.data)).catch(() => setMe({}));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-6" data-testid="legal-docs-page">
      <PageHeader title="Legalitas & Dokumen" subtitle="Legalitas organisasi diperiksa PT SJI. Dokumen penyerahan/penguasaan lahan hanya disimpan dan dipantau, tanpa persetujuan PT SJI." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="legal-status-summary">
        <StatusCard label="Legalitas Organisasi" value={me?.legalStatus} testid="org-legal-status" />
        {lands.map((l) => <StatusCard key={l.id} label={`Dokumen Lahan ${l.landCode}`} value={l.landDocStatus === "UPLOADED" ? "UPLOADED" : "NOT_UPLOADED"} testid={`land-legal-status-${l.id}`} />)}
      </div>
      <UploadForm lands={lands} onDone={load} />
      <h2 className="text-base md:text-lg font-semibold text-slate-900">Status Pengajuan</h2>
      {!rows ? <Loader2 className="h-6 w-6 animate-spin text-emerald-700" /> : (
        <ResponsiveTable testid="legal-table" rows={rows} empty="Belum ada dokumen yang diajukan."
          columns={[{ key: "createdAt", label: "Tanggal", render: (r) => formatDate(r.createdAt) },
            { key: "docType", label: "Dokumen", render: docLabel }, { key: "detail", label: "Detail", render: detail },
            { key: "file", label: "File", render: (r) => <FileLinks r={r} /> }, statusCol]} />
      )}
    </div>
  );
}

const Review = ({ row, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (status) => {
    if (status !== "VERIFIED" && !note.trim()) return toast.error("Tulis catatan untuk kelompok tani");
    setBusy(true);
    try { await api.post(`/admin/legal-docs/${row.id}/review`, { status, note }); toast.success("Keputusan tersimpan dan terkirim ke kelompok tani"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5 min-w-[230px]">
      <Textarea rows={2} placeholder="Catatan PT SJI (wajib untuk perbaikan / tolak)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`legal-note-${row.id}`} />
      <div className="flex flex-wrap gap-1">
        <Button size="sm" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" onClick={() => act("VERIFIED")} data-testid={`legal-verify-${row.id}`}>Setujui</Button>
        <Button size="sm" disabled={busy} variant="outline" onClick={() => act("REVISION_REQUIRED")} data-testid={`legal-revision-${row.id}`}>Minta Perbaikan</Button>
        <Button size="sm" disabled={busy} variant="outline" className="text-red-700 border-red-200" onClick={() => act("REJECTED")} data-testid={`legal-reject-${row.id}`}>Tolak</Button>
      </div>
    </div>
  );
};

const MonitorNote = ({ row, onDone }) => {
  const [note, setNote] = useState(row.note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!note.trim()) return toast.error("Tulis catatan pemantauan");
    setBusy(true);
    try { await api.post(`/admin/legal-docs/${row.id}/note`, { note }); toast.success("Catatan pemantauan tersimpan"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5 min-w-[220px]" data-testid={`legal-monitor-only-${row.id}`}>
      <p className="text-xs text-slate-500">Pemantauan saja · tanpa persetujuan</p>
      <Textarea rows={2} placeholder="Catatan pemantauan PT SJI (opsional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`land-doc-note-${row.id}`} />
      <Button size="sm" variant="outline" disabled={busy} onClick={save} data-testid={`land-doc-note-save-${row.id}`}>Simpan Catatan</Button>
    </div>
  );
};

const Preview = ({ doc, onClose }) => (
  <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-3xl" data-testid="legal-preview-dialog">
      <DialogHeader><DialogTitle>{doc && `${doc.farmerGroupName} · ${docLabel(doc)}`}</DialogTitle></DialogHeader>
      {doc && (doc.contentType === "application/pdf"
        ? <iframe title="Dokumen" src={fileUrl(doc)} className="w-full h-[70vh] rounded border" data-testid="legal-preview-pdf" />
        : <img src={fileUrl(doc)} alt="Dokumen" className="max-h-[70vh] mx-auto rounded border" data-testid="legal-preview-img" />)}
      {doc && <a href={fileUrl(doc, true)} className="inline-flex items-center gap-1 text-emerald-800 font-semibold text-sm" data-testid="legal-preview-download"><Download className="h-4 w-4" />Unduh dokumen</a>}
    </DialogContent>
  </Dialog>
);

const Monitoring = ({ groups }) => (
  <section className="space-y-2" data-testid="legal-monitoring">
    <h2 className="text-base md:text-lg font-semibold text-slate-900">Pemantauan Organisasi ({groups.length})</h2>
    <p className="text-xs text-slate-500">Semua Kelompok Tani / Koperasi terdaftar. "Belum Mengajukan Dokumen" berarti organisasi belum mengunggah legalitas, sehingga belum ada yang bisa diperiksa.</p>
    <ResponsiveTable testid="legal-monitoring-table" rows={groups} empty="Belum ada organisasi terdaftar."
      columns={[{ key: "name", label: "Organisasi" },
        { key: "verificationStatus", label: "Verifikasi Identitas", render: (g) => <StatusBadge value={g.verificationStatus} testid={`mon-identity-${g.id}`} /> },
        { key: "legalStatus", label: "Legalitas Organisasi", render: (g) => <LegalBadge value={g.legalStatus} testid={`mon-legal-${g.id}`} /> },
        { key: "docs", label: "Dokumen", render: (g) => <span data-testid={`mon-docs-${g.id}`}>{g.docCount} diunggah{g.pendingDocs ? ` · ${g.pendingDocs} menunggu` : ""}</span> },
        { key: "lands", label: "Dokumen Lahan (pemantauan)", render: (g) => (!g.lands.length ? "-" : <div className="flex flex-col gap-1">{g.lands.map((l) => <span key={l.id} className="text-xs">{l.landCode}: <LegalBadge value={l.landDocStatus === "UPLOADED" ? "UPLOADED" : "NOT_UPLOADED"} testid={`mon-land-${l.id}`} /></span>)}</div>) }]} />
  </section>
);

const QUEUES = [["ORG_LEGAL", "Antrean Legalitas Organisasi"], ["LAND_CONTROL", "Dokumen Lahan (Pemantauan)"], ["ALL", "Semua Dokumen"]];

export const VerifiedGroups = ({ groups }) => {
  const list = groups.filter((g) => g.legalStatus === "VERIFIED");
  return (
    <div className="bg-white border rounded-xl p-5" data-testid="verified-groups">
      <h2 className="text-base md:text-lg font-semibold text-slate-900">Kelompok Tani / Koperasi Terverifikasi ({list.length})</h2>
      {!list.length ? <p className="mt-2 text-sm text-slate-500">Belum ada organisasi dengan legalitas terverifikasi.</p> : (
        <ul className="mt-3 divide-y">
          {list.map((g) => (
            <li key={g.id} className="py-2.5 flex flex-wrap items-center gap-2 justify-between" data-testid={`verified-group-${g.id}`}>
              <div><p className="font-semibold text-slate-900">{g.name}</p><p className="text-xs text-slate-500">{g.region || "-"} · {g.lands.length} lahan · {g.lands.filter((l) => l.landDocStatus === "UPLOADED").length} lahan dengan dokumen diunggah</p></div>
              <StatusBadge value="VERIFIED" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

function AdminLegal() {
  const [kind, setKind] = useState("ORG_LEGAL");
  const [rows, setRows] = useState(null);
  const [groups, setGroups] = useState([]);
  const [preview, setPreview] = useState(null);
  const load = useCallback((alive = { on: true }) => {
    setRows(null);
    api.get("/legal-docs", { params: kind === "ALL" ? {} : kind === "LAND_CONTROL" ? { kind } : { kind, status: "PENDING" } })
      .then((r) => { if (alive.on) setRows(r.data); }).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/legal-summary").then((r) => { if (alive.on) setGroups(r.data); }).catch(() => {});
  }, [kind]);
  useEffect(() => {
    const alive = { on: true };
    load(alive);
    return () => { alive.on = false; };
  }, [load]);
  return (
    <div className="space-y-6" data-testid="legal-docs-page">
      <PageHeader title="Legalitas Organisasi & Pemantauan Dokumen Lahan" subtitle="PT SJI memeriksa legalitas organisasi. Dokumen lahan hanya dipantau: PT SJI tidak mengesahkan kepemilikan atau legalitas lahan." />
      {kind === "LAND_CONTROL" && <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs sm:text-sm text-amber-900" data-testid="land-doc-monitor-note">Mode pemantauan: dokumen lahan diunggah dan menjadi tanggung jawab Kelompok Tani/Koperasi. Tidak ada tombol setujui/tolak.</p>}
      <div className="flex flex-wrap gap-2" data-testid="legal-queue-tabs">
        {QUEUES.map(([k, l]) => <button key={k} type="button" onClick={() => setKind(k)} className={`h-10 px-4 rounded-full border text-sm font-semibold ${kind === k ? "bg-emerald-800 border-emerald-800 text-white" : "bg-white text-emerald-900"}`} data-testid={`legal-tab-${k}`}>{l}</button>)}
      </div>
      {!rows ? <Loader2 className="h-6 w-6 animate-spin text-emerald-700" /> : (
        <ResponsiveTable testid="legal-table" rows={rows} empty={kind === "ORG_LEGAL" ? "Tidak ada dokumen yang menunggu pemeriksaan. Organisasi yang belum mengunggah dokumen tampil di Pemantauan Organisasi di bawah." : "Belum ada dokumen."}
          columns={[{ key: "createdAt", label: "Diajukan", render: (r) => formatDate(r.createdAt) },
            { key: "farmerGroupName", label: "Kelompok Tani / Koperasi" },
            { key: "docType", label: "Jenis Dokumen", render: docLabel }, { key: "detail", label: "Detail", render: detail },
            { key: "file", label: "Dokumen", render: (r) => <FileLinks r={r} onPreview={setPreview} /> }, statusCol,
            { key: "act", label: "Keputusan", render: (r) => (r.kind === "LAND_CONTROL" ? <MonitorNote row={r} onDone={load} /> : ["SUBMITTED", "UNDER_REVIEW"].includes(r.status) ? <Review row={r} onDone={load} /> : <span className="text-xs text-slate-500">Sudah diputuskan</span>) }]} />
      )}
      <Monitoring groups={groups} />
      <VerifiedGroups groups={groups} />
      <Preview doc={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

export default function LegalDocsPage() {
  const { user } = useAuth();
  return user.role === "SUPER_ADMIN" ? <AdminLegal /> : <FarmerLegal user={user} />;
}
